import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";

// Stripe Checkout runs the whole form, e-mail included, and takes the payment
// itself. Nothing is mounted beside it and there is no confirm step of ours
// to reach.
export default class extends Controller {
  static targets = ["element", "error"];
  static values = { publishableKey: String, createUrl: String, genericError: String, locale: String, plan: String, currency: String, value: Number };

  // A Turbo preview is a cached copy shown while the real page loads, and
  // the real page connects this controller again: starting here too would
  // open a second Stripe session and record a second InitiateCheckout.
  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;
    this.start();
  }

  async start() {
    this.errorTarget.textContent = "";
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({ currency: this.currencyValue, plan: this.planValue })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      this.errorTarget.textContent = body.error || this.genericErrorValue;
      return;
    }

    // A blocked script or an offline browser fails here and the container
    // stays empty. Without this the failure is silent: the visitor sees a
    // blank box with nothing saying the payment never loaded. The hosted link
    // under the panel is where they go instead.
    try {
      const stripe = await loadStripe(this.publishableKeyValue, { locale: this.localeValue });
      if (!stripe) throw new Error("Stripe.js did not load");
      this.checkout = await stripe.createEmbeddedCheckoutPage({ clientSecret: body.client_secret });
      this.checkout.mount(this.elementTarget);
      // Queued, not sent: the pixel drains the queue only once marketing
      // consent has loaded it (see shared/_consent_scripts). The event id is
      // the one the server recorded, so Meta folds the two into one.
      if (body.event_id) {
        (window.oremusFbq = window.oremusFbq || []).push(["track", "InitiateCheckout", { value: this.valueValue, currency: this.currencyValue.toUpperCase() }, { eventID: body.event_id }]);
      }
    } catch {
      this.errorTarget.textContent = this.genericErrorValue;
    }
  }

  disconnect() {
    this.checkout?.destroy();
  }
}
